import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  Stack,
  Typography,
} from '@mui/joy';
import React, { useRef } from 'react';
import { BiPrinter } from 'react-icons/bi';

import { useFetchOrganizationOfCurrentUser } from '@MP/shared/api/hooks/auth';
import { RectangleButton, RoundButton } from '@MP/shared/components/Button';

/**
 * iframe 문서를 인쇄한다. 인쇄 직전에 문서 title 을 지정한 값으로 바꿔
 * 브라우저의 "PDF로 저장" 파일명으로 제안되게 하고, 인쇄가 끝나면 원복한다.
 *
 * same-origin iframe 을 인쇄할 때 Chrome 은 iframe 문서가 아니라 최상위(부모) 페이지의
 * document.title 을 파일명으로 사용하므로, 부모 문서와 iframe 문서 title 을 모두 바꾼다.
 * (파일명은 제안값일 뿐이며 사용자가 저장 대화상자에서 변경할 수 있고, 브라우저마다 형식이 다를 수 있다.)
 */
const printIframeWithFileName = (iframe: HTMLIFrameElement | null, fileName: string) => {
  const win = iframe?.contentWindow;
  if (!win) return;

  const prevParentTitle = document.title;
  const prevIframeTitle = win.document.title;
  document.title = fileName;
  win.document.title = fileName;

  const restore = () => {
    document.title = prevParentTitle;
    win.document.title = prevIframeTitle;
    win.removeEventListener('afterprint', restore);
    window.removeEventListener('afterprint', restore);
  };
  win.addEventListener('afterprint', restore);
  window.addEventListener('afterprint', restore);

  win.focus();
  win.print();
};

/**
 * STIM+ 비급여 동의서 양식을 출력하는 버튼 + 모달.
 * 환자 상세 상단 툴바(STIM 탭 활성화 시)에서 사용한다.
 */
const StimConsentPrintButton = () => {
  const [open, setOpen] = React.useState(false);
  // localStorage.getItem은 미저장 시 null(→ Number(null)===0)이므로
  // 저장값이 없으면 price를 undefined로 유지해 인쇄 버튼이 비활성 상태가 되도록 한다.
  const [price, setPrice] = React.useState<number | undefined>(() => {
    try {
      const stored = localStorage.getItem('stim_price_in_consent');
      if (stored !== null && stored !== '' && isFinite(Number(stored))) {
        return Number(stored);
      }
    } catch (e) {
      console.warn('[StimConsentPrintButton] localStorage.getItem 실패', e);
    }
    return undefined;
  });
  const stimParticipantInformationRef = useRef<HTMLIFrameElement>(null);
  const stimProvisionAgreementRef = useRef<HTMLIFrameElement>(null);
  const { data: org } = useFetchOrganizationOfCurrentUser();

  const handleClose = () => {
    try {
      if (isFinite(Number(price))) {
        localStorage.setItem('stim_price_in_consent', price?.toString() || '');
      }
    } catch (e) {
      console.warn('[StimConsentPrintButton] localStorage.setItem 실패', e);
    }
    setOpen(false);
  };

  return (
    <>
      <Button variant="plain" color="neutral" startDecorator={<BiPrinter size={16} />} onClick={() => setOpen(true)}>
        <Typography sx={{ fontSize: '14px', fontWeight: '400' }}>STIM+ 비급여 동의서 양식 출력</Typography>
      </Button>

      <Modal open={open}>
        <ModalDialog>
          <Box>
            <Typography level="h4" fontWeight={600}>
              STIM+ 비급여 동의서 양식 출력
            </Typography>
            <Typography level="body-sm">아래의 정보는 동의서 양식에 포함되는 정보입니다.</Typography>
          </Box>
          <DialogContent>
            <Stack width={500} spacing={'8px'}>
              <Stack direction={'row'} spacing={'24px'}>
                <FormControl sx={{ flexGrow: 1 }}>
                  <FormLabel>병원 명</FormLabel>
                  <Input value={org?.name} readOnly disabled />
                </FormControl>
                <FormControl sx={{ flexGrow: 1 }}>
                  <FormLabel>병원 전화번호</FormLabel>
                  <Input value={org?.tel} readOnly disabled />
                </FormControl>
              </Stack>
              <Stack direction={'row'} spacing={'24px'} pb={'16px'}>
                <FormControl required sx={{ flexGrow: 1, flexBasis: 0 }}>
                  <FormLabel>금액</FormLabel>
                  <Input
                    value={price?.toString() || ''}
                    onChange={(e) => {
                      const numPrice = Number(e.target.value);
                      if (e.target.value === '0' || (isFinite(numPrice) && numPrice > 0)) {
                        setPrice(numPrice);
                      } else {
                        setPrice(undefined);
                      }
                    }}
                  />
                </FormControl>
                <FormControl sx={{ flexGrow: 1, flexBasis: 0 }}></FormControl>
              </Stack>
              <Divider />
              <Stack>
                <RoundButton
                  variant="plain"
                  size="sm"
                  color="neutral"
                  startDecorator={<BiPrinter />}
                  disabled={price === undefined}
                  onClick={() =>
                    printIframeWithFileName(
                      stimProvisionAgreementRef.current,
                      '평가 유예 신의료기술 참여 및 개인정보 수집・이용 제3자 제공 동의서'
                    )
                  }>
                  <Typography level="body-sm" fontWeight={400} sx={{ color: 'inherit' }}>
                    개인정보 수집 이용 제3자 제공 동의서
                  </Typography>
                </RoundButton>
                <RoundButton
                  variant="plain"
                  color="neutral"
                  size="sm"
                  disabled={price === undefined}
                  startDecorator={<BiPrinter />}
                  onClick={() =>
                    printIframeWithFileName(stimParticipantInformationRef.current, '평가 유예 신의료기술 대상자 설명문')
                  }>
                  <Typography level="body-sm" fontWeight={400} sx={{ color: 'inherit' }}>
                    대상자 설명문
                  </Typography>
                </RoundButton>
              </Stack>
            </Stack>
            <iframe
              ref={stimParticipantInformationRef}
              title="stimParticipantInformation"
              src={`/agreements/template/stimParticipantInformation.html?hospital_name=${org?.name}&hospital_tel=${org?.tel}&price=${price}`}
              style={{ width: 0, height: 0, border: 'none' }}></iframe>
            <iframe
              ref={stimProvisionAgreementRef}
              title="stimProvisionAgreement"
              src={`/agreements/template/stimProvisionAgreement.html?hospital_name=${org?.name}`}
              style={{ width: 0, height: 0, border: 'none' }}></iframe>
          </DialogContent>
          <DialogActions sx={{ pt: 0 }}>
            <RectangleButton onClick={handleClose}>닫기</RectangleButton>
          </DialogActions>
        </ModalDialog>
      </Modal>
    </>
  );
};

export default StimConsentPrintButton;
